/* eslint-disable camelcase */
import React, { useState, useRef, useEffect } from 'react';

import { useRouter } from 'next/router';
import { FaChevronDown } from 'react-icons/fa';
import styled from 'styled-components';

import { useMembership } from '~/techstyle-shared/react-accounts';
import { useBreakpoint } from '~/techstyle-shared/react-components';
import { FormattedMessage } from '~/techstyle-shared/react-intl';

import useGridInputs from '../hooks/useGridInputs';
import useProductGridData from '../hooks/useProductGridData';
import { scrollToTopOfGrid } from '../utils/scroll';
import {
  getSanitizedSortOptions,
  getSortOptionRbId,
  isSortOptionSelected,
  updateSortInUrlParams,
} from '../utils/sortOptions';

const DynamicFormattedMessage = FormattedMessage;

const GridSortDropdownContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
  white-space: nowrap;
  flex-shrink: 0;
  ${({ theme }) => theme.gridFilterStyles.listingFilterStyle};
`;

const GridSortDropdownLabel = styled.label`
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.gridSortDropdownLabelTextColor};
  flex-shrink: 0;
  font-family: inherit;
  ${({ theme }) => theme.gridFilterStyles.desktopSortLabelStyle};
`;

const GridSortDropdownButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 24px; /* This enforces 24px between Selected Sort Dropdown display_name and chevron */
  min-width: 100px;
  padding: 0.5rem;
  font-size: 0.875rem;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.gridSortDropdownSortButtonTextColor};
  background-color: white;
  border: 1px solid
    ${({ theme }) => theme.colors.gridSortDropdownSortButtonBorderColor};
  border-radius: 2px;
  cursor: pointer;
  font-family: inherit;
  ${({ theme }) => theme.gridFilterStyles.desktopSortButtonStyle};
`;

const Chevron = styled(FaChevronDown)`
  font-size: 0.5rem;
  color: black;
  flex-shrink: 0;
  transform: ${({ open }) => (open ? 'rotate(180deg)' : 'rotate(0deg)')};
`;

const GridSortDropdownList = styled.ul`
  position: absolute;
  top: 98%;
  left: auto;
  right: 0;
  min-width: 120px;
  width: 100%;
  background: white;
  border: 1px solid
    ${({ theme }) => theme.colors.gridSortDropdownSortButtonBorderColor};
  border-radius: 2px;
  z-index: 2; /* 1 clashes with favorites button, 10 clashes with navbar/mini-cart */
  max-height: 200px;
  overflow-y: auto;
  box-shadow: 0px 0px 5px rgba(0, 0, 0, 0.1);
`;

const GridSortDropdownOption = styled.li`
  padding: 0.5rem;
  font-size: 0.875rem;
  text-align: left;
  width: 100%;
  background-color: white;
  color: ${({ theme }) => theme.colors.gridSortDropdownItemTextColorDefault};
  cursor: pointer;
  &:hover {
    background-color: ${({ theme }) =>
      theme.colors.gridSortDropdownItemBackgroundColorHover};
    color: ${({ theme }) => theme.colors.gridSortDropdownItemTextColorHover};
  }
`;

function DesktopGridSortDropdown() {
  const router = useRouter();
  const { isVip } = useMembership();
  const { sortOptions: builderSortOptions } = useGridInputs();
  const { cioSortOptions } = useProductGridData();
  const [isOpen, setIsOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(0);
  const buttonRef = useRef();
  const { isMobile } = useBreakpoint();

  // Close the dropdown if the user clicks outside of it
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        buttonRef.current &&
        !buttonRef.current.contains(event.target) &&
        isOpen
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [isOpen]);

  if (!cioSortOptions) {
    return null;
  }

  const selectedSortOption = cioSortOptions.find(({ status }) =>
    isSortOptionSelected({ status })
  );

  const sanitizedSortOptions = getSanitizedSortOptions({
    builderSortOptions,
    cioSortOptions,
    isVip,
    excludeSelectedOption: true,
  });

  const handleSortOptionClick = (key) => {
    updateSortInUrlParams({
      router,
      sort: key,
    });
    setIsOpen(false);
    scrollToTopOfGrid({ isMobile });
  };

  const handleKeyDown = (e) => {
    if (!isOpen) {
      return;
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setFocusedIndex((prev) => (prev + 1) % sanitizedSortOptions.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setFocusedIndex(
        (prev) =>
          (prev - 1 + sanitizedSortOptions.length) % sanitizedSortOptions.length
      );
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleSortOptionClick(sanitizedSortOptions[focusedIndex].sort_by);
    } else if (e.key === 'Escape') {
      setIsOpen(false);
    }
  };

  return (
    <GridSortDropdownContainer>
      <GridSortDropdownLabel id="sort-label">
        <FormattedMessage
          id="site_product_grid.sort_label"
          defaultMessage="Sort"
        />
      </GridSortDropdownLabel>
      <GridSortDropdownButton
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-labelledby="sort-label"
        onClick={() => setIsOpen(!isOpen)}
        onKeyDown={handleKeyDown}
        ref={buttonRef}
        data-autotag="sort-dropdown"
      >
        <DynamicFormattedMessage
          id={`site_product_filter.${getSortOptionRbId(
            `sort_${selectedSortOption?.sort_by}`
          )}`}
          defaultMessage={selectedSortOption?.display_name || 'Select'}
          whiteSpace="nowrap"
        />
        <Chevron open={isOpen} />
      </GridSortDropdownButton>
      {isOpen && sanitizedSortOptions?.length > 0 && (
        <GridSortDropdownList
          role="listbox"
          aria-labelledby="sort-label"
          aria-orientation="vertical"
        >
          {sanitizedSortOptions.map(({ display_name, sort_by }, index) => (
            <GridSortDropdownOption
              key={sort_by}
              role="option"
              aria-selected={focusedIndex === index}
              tabIndex={-1}
              focused={focusedIndex === index}
              onMouseDown={() => handleSortOptionClick(sort_by)}
              data-autotag={`sort-by-${sort_by}`}
            >
              <DynamicFormattedMessage
                id={`site_product_filter.${getSortOptionRbId(
                  `sort_${sort_by}`
                )}`}
                defaultMessage={display_name}
              />
            </GridSortDropdownOption>
          ))}
        </GridSortDropdownList>
      )}
    </GridSortDropdownContainer>
  );
}

export default DesktopGridSortDropdown;
